import React, { type FC, useEffect } from 'react';
import { defineMessages, type useIntl } from 'react-intl';

import AvatarGroup, { type AvatarProps } from '@atlaskit/avatar-group';

import { type ShowFlagArgs } from '@confluence/flags/entry-points/FlagsStateContainer.ts';

import { ResourceType } from '../resolveRestrictions/queries/__types__/BlockedAccessRestrictionsQuery.ts';
import type { RestrictedUserPrincipal } from '../commonTypes.tsx';

import type { AccessRequestsList } from './realtimeAccessRequestTypes.ts';
import type { ResourceAccessType } from './__generated__/useRealtimeAccessRequestsFlagsQuery.graphql.ts';

const i18n = defineMessages({
	singleRequestTitleView: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.single-request-title-view',
		defaultMessage: '{name} wants to view this content',
		description: 'Title of the flag when a single user requests view access',
	},
	singleRequestTitleEdit: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.single-request-title-edit',
		defaultMessage: '{name} wants to edit this content',
		description: 'Title of the flag when a single user requests edit access',
	},
	twoRequestsTitle: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.two-requests-title',
		defaultMessage: '{firstName} and {secondName} want to access this content',
		description: 'Title of the flag when two users request access',
	},
	multipleRequestsTitle: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.multiple-requests-title',
		defaultMessage: '{firstName} and {remainingCount} others want to access this content',
		description: 'Title of the flag when multiple users request access',
	},
	spaceAndParentRestriction: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.space-and-parent-restriction',
		defaultMessage:
			"They'll also need to be added to the space and to {parentRestrictionCount, plural, one {# parent item} other {# parent items}}.",
		description: 'Description of restrictions when both space and parent restrictions exist',
	},
	spaceRestrictionOnly: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.space-restriction-only',
		defaultMessage: "They'll also need to be added to the space.",
		description: 'Description of restrictions when only space restriction exists',
	},
	parentRestrictionOnly: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.parent-restriction-only',
		defaultMessage:
			"They'll also need to be added to {parentRestrictionCount, plural, one {# parent item} other {# parent items}}.",
		description: 'Description of restrictions when only parent restriction exists',
	},
	approveButton: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.approve-button',
		defaultMessage: 'Approve',
		description:
			'Button label to approve a pending access request in the realtime notification flag',
	},
	viewDetailsButton: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.view-details-button',
		defaultMessage: 'View details',
		description: 'Button label to view full details of an access request in a modal dialog',
	},
	denyButton: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.deny-button',
		defaultMessage: 'Deny',
		description: 'Button label to deny or reject a pending access request in the notification flag',
	},
	reviewRequestsButton: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.review-requests-button',
		defaultMessage: 'Review requests',
		description: 'Label for review requests button',
	},
	successSpaceAndParentRestrictionView: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.success-space-and-parent-restriction-view',
		defaultMessage:
			'{displayName} was added to the space and to {parentRestrictionCount, plural, one {# parent item} other {# parent items}}. They can now view this content.',
		description:
			'Success message when user is added with space and parent restrictions for view access',
	},
	successSpaceAndParentRestrictionEdit: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.success-space-and-parent-restriction-edit',
		defaultMessage:
			'{displayName} was added to the space and to {parentRestrictionCount, plural, one {# parent item} other {# parent items}}. They can now edit this content.',
		description:
			'Success message when user is added with space and parent restrictions for edit access',
	},
	successSpaceRestrictionOnlyView: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.success-space-restriction-only-view',
		defaultMessage: '{displayName} was added to the space and can now view this content.',
		description: 'Success message when user is added with only space restriction for view access',
	},
	successSpaceRestrictionOnlyEdit: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.success-space-restriction-only-edit',
		defaultMessage: '{displayName} was added to the space and can now edit this content.',
		description: 'Success message when user is added with only space restriction for edit access',
	},
	successParentRestrictionOnlyView: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.success-parent-restriction-only-view',
		defaultMessage:
			'{displayName} was added to {parentRestrictionCount, plural, one {# parent item} other {# parent items}} and can now view this content.',
		description: 'Success message when user is added with only parent restrictions for view access',
	},
	successParentRestrictionOnlyEdit: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.success-parent-restriction-only-edit',
		defaultMessage:
			'{displayName} was added to {parentRestrictionCount, plural, one {# parent item} other {# parent items}} and can now edit this content.',
		description: 'Success message when user is added with only parent restrictions for edit access',
	},
	successNoRestrictionView: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.success-no-restriction-view',
		defaultMessage: '{displayName} can now view this content.',
		description: 'Success message when user is added with no restrictions for view access',
	},
	successNoRestrictionEdit: {
		id: 'share-and-restrict-dialog.realtime-access-request-flag.success-no-restriction-edit',
		defaultMessage: '{displayName} can now edit this content.',
		description: 'Success message when user is added with no restrictions for edit access',
	},
});

/**
 * Assumes requests are already filtered to only those
 * That have displayName and requestAccessType
 * */
type FormatMessage = ReturnType<typeof useIntl>['formatMessage'];

export const makeFlagTitle = (
	requests: AccessRequestsList,
	formatMessage: FormatMessage,
): string => {
	if (requests.length === 1) {
		const onlyRequest = requests[0];
		const name = onlyRequest?.user?.displayName!;
		const accessType = onlyRequest?.requestAccessType!;

		const messageKey =
			accessType === 'EDIT' ? i18n.singleRequestTitleEdit : i18n.singleRequestTitleView;

		return formatMessage(messageKey, {
			name,
		});
	}

	if (requests.length > 1) {
		const firstName = requests[0]?.user?.displayName!;

		if (requests.length === 2) {
			const secondName = requests[1]?.user?.displayName!;
			return formatMessage(i18n.twoRequestsTitle, {
				firstName,
				secondName,
			});
		}

		const remainingCount = requests.length - 1;
		return formatMessage(i18n.multipleRequestsTitle, {
			firstName,
			remainingCount,
		});
	}

	// Should not happen, because method gets invoked if there's at least one request
	return '';
};

export const extractRestrictionInfo = (
	restrictedUserPrincipalInfo: RestrictedUserPrincipal | undefined,
) => {
	const hasSpaceRestriction =
		restrictedUserPrincipalInfo?.restrictedResources?.some(
			(resource) => resource?.resourceType === ResourceType.SPACE,
		) ?? false;

	const parentRestrictionCount =
		restrictedUserPrincipalInfo?.restrictedResources?.reduce((res, curr) => {
			const isParentRestriction = curr?.resourceType !== ResourceType.SPACE;
			return res + (isParentRestriction ? 1 : 0);
		}, 0) ?? 0;

	const hasParentRestriction = parentRestrictionCount > 0;
	const isBlocked = hasSpaceRestriction || hasParentRestriction;

	return {
		isBlocked,
		hasSpaceRestriction,
		hasParentRestriction,
		parentRestrictionCount,
	};
};

export const makeFlagDescription = (
	requests: AccessRequestsList,
	formatMessage: FormatMessage,
	restrictedUserPrincipalInfo?: RestrictedUserPrincipal,
): string | null => {
	if (requests.length === 1 && restrictedUserPrincipalInfo) {
		const { hasSpaceRestriction, hasParentRestriction, parentRestrictionCount } =
			extractRestrictionInfo(restrictedUserPrincipalInfo);

		if (hasSpaceRestriction && hasParentRestriction) {
			return formatMessage(i18n.spaceAndParentRestriction, {
				parentRestrictionCount,
			});
		} else if (hasSpaceRestriction) {
			return formatMessage(i18n.spaceRestrictionOnly);
		} else if (hasParentRestriction) {
			return formatMessage(i18n.parentRestrictionOnly, {
				parentRestrictionCount,
			});
		}
	}

	return null;
};

export const makeFlagCustomIcon = (requests: AccessRequestsList) => {
	const avatarData = requests.flatMap<AvatarProps>((request) =>
		request?.user?.displayName && request?.requestAccessType && request?.user?.id
			? {
					name: request.user.displayName,
					src: request.user.photos?.find((photo) => photo?.isPrimary)?.value ?? undefined,
					key: request.user.id,
				}
			: [],
	);

	const maxCount = avatarData.length === 1 ? 1 : 2;
	return <AvatarGroup data={avatarData} maxCount={maxCount} />;
};

export const makeFlagActions = (
	requests: AccessRequestsList,
	restrictedUserPrincipalInfo: RestrictedUserPrincipal | undefined,
	processRequest: (
		request: AccessRequestsList[number],
		decision: 'APPROVE' | 'DENY',
		restrictedUserPrincipalInfo: RestrictedUserPrincipal | undefined,
	) => void,
	onReview: () => void,
	onViewDetails: () => void,
	hideFlag: () => void,
	formatMessage: FormatMessage,
): ShowFlagArgs['actions'] => {
	if (requests.length === 1) {
		const approveRequest = () => {
			processRequest(requests[0], 'APPROVE', restrictedUserPrincipalInfo);
			hideFlag();
		};

		const denyRequest = () => {
			processRequest(requests[0], 'DENY', undefined);
			hideFlag();
		};

		const primaryAction = {
			content: formatMessage(i18n.approveButton),
			onClick: approveRequest,
		};

		const secondaryAction = restrictedUserPrincipalInfo
			? {
					content: formatMessage(i18n.viewDetailsButton),
					onClick: onViewDetails,
				}
			: {
					content: formatMessage(i18n.denyButton),
					onClick: denyRequest,
				};

		return [primaryAction, secondaryAction];
	}

	if (requests.length > 1) {
		return [
			{
				content: formatMessage(i18n.reviewRequestsButton),
				onClick: onReview,
			},
		];
	}

	return [];
};

export const makeSuccessFlagDescription = (
	restrictedUserPrincipalInfo: RestrictedUserPrincipal | undefined,
	displayName: string,
	accessType: ResourceAccessType,
	formatMessage: FormatMessage,
): string => {
	const { hasSpaceRestriction, hasParentRestriction, parentRestrictionCount } =
		extractRestrictionInfo(restrictedUserPrincipalInfo);

	if (hasSpaceRestriction && hasParentRestriction) {
		const messageKey =
			accessType === 'EDIT'
				? i18n.successSpaceAndParentRestrictionEdit
				: i18n.successSpaceAndParentRestrictionView;
		return formatMessage(messageKey, {
			displayName,
			parentRestrictionCount,
		});
	} else if (hasSpaceRestriction) {
		const messageKey =
			accessType === 'EDIT'
				? i18n.successSpaceRestrictionOnlyEdit
				: i18n.successSpaceRestrictionOnlyView;
		return formatMessage(messageKey, {
			displayName,
		});
	} else if (hasParentRestriction) {
		const messageKey =
			accessType === 'EDIT'
				? i18n.successParentRestrictionOnlyEdit
				: i18n.successParentRestrictionOnlyView;
		return formatMessage(messageKey, {
			displayName,
			parentRestrictionCount,
		});
	}

	const messageKey =
		accessType === 'EDIT' ? i18n.successNoRestrictionEdit : i18n.successNoRestrictionView;
	return formatMessage(messageKey, {
		displayName,
	});
};

export const FlagRemovalDetector: FC<{ onDestroy: () => void; children: React.ReactNode }> = ({
	onDestroy,
	children,
}) => {
	useEffect(() => onDestroy, [onDestroy]);
	return children;
};

export const makeFlagIdSuffix = (requests: AccessRequestsList): string =>
	requests.reduce((result, current) => result + (current?.id ?? ''), '');
